import { useMemo, useState, type ReactNode } from 'react'
import { AlertCircle, Info, Loader2, Plus, Trash2 } from 'lucide-react'
import axios from 'axios'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { CurrencyInput, RateInput } from './RateInput'
import {
    BTCIcon,
    DOGEIcon,
    ETHIcon,
    TRONIcon,
    USDCIcon,
    USDTIcon,
} from './CryptoIcons'
import {
    useCreateBufferTier,
    useCryptoBufferConfigs,
    useCryptocurrencies,
    useDeleteBufferTier,
    usePatchCryptoBuffer,
} from '@/hooks/useCryptoBuffers'
import type {
    BufferOrderType,
    CryptoBufferConfig,
    CryptoSummary,
} from '@/types/crypto-buffer'

const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as {
            message?: string
            errors?: string[]
        }
        if (data?.errors?.length) return data.errors.join(', ')
        return data?.message || error.message
    }
    return error instanceof Error
        ? error.message
        : 'An unexpected error occurred'
}

const ICONS: Record<string, ReactNode> = {
    btc: <BTCIcon />,
    doge: <DOGEIcon />,
    eth: <ETHIcon />,
    trx: <TRONIcon />,
    usdc: <USDCIcon />,
    usdt: <USDTIcon />,
}

const CoinIcon = ({
    crypto,
}: {
    crypto: Pick<CryptoSummary, 'symbol' | 'logoUrl'>
}) => {
    const icon = ICONS[crypto.symbol.toLowerCase()]
    if (icon) return <>{icon}</>
    if (crypto.logoUrl)
        return (
            <img
                src={crypto.logoUrl}
                alt={crypto.symbol}
                className="h-5 w-5 rounded-full"
            />
        )
    return (
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold uppercase">
            {crypto.symbol.slice(0, 1)}
        </span>
    )
}

const num = (v: number | string | null | undefined) => {
    const n = Number(v ?? 0)
    return Number.isFinite(n) ? n : 0
}

/** A coin can be quoted when it has a positive default buffer or any tier. */
const isConfigured = (config?: CryptoBufferConfig, summary?: CryptoSummary) =>
    (config?.tiers.length ?? 0) > 0 ||
    num(config?.defaultBufferPercent ?? summary?.defaultBufferPercent) > 0

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export const CryptoBuffersTab = () => {
    const { data: cryptos, isLoading, error } = useCryptocurrencies()
    const ids = useMemo(() => (cryptos ?? []).map(c => c.id), [cryptos])
    const configs = useCryptoBufferConfigs(ids)
    const [selectedId, setSelectedId] = useState<string | null>(null)

    const sorted = useMemo(
        () =>
            [...(cryptos ?? [])].sort((a, b) =>
                a.symbol.localeCompare(b.symbol)
            ),
        [cryptos]
    )
    const selected =
        sorted.find(c => c.id === selectedId) ??
        sorted.find(c => !isConfigured(configs.byId[c.id], c)) ??
        sorted[0]
    const unconfigured = sorted.filter(
        c => configs.byId[c.id] && !isConfigured(configs.byId[c.id], c)
    )

    if (isLoading) {
        return (
            <div className="space-y-8">
                <Skeleton className="h-8 w-full rounded-full" />
                <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-5">
                    {Array.from({ length: 10 }).map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                    ))}
                </div>
            </div>
        )
    }

    if (error || !cryptos) {
        return (
            <div className="flex h-48 items-center justify-center">
                <p className="text-sm text-destructive">
                    Failed to load cryptocurrencies.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-8">
            <div className="flex h-8 items-center rounded-full bg-[#5FA8B4] px-6">
                <h2 className="text-base font-medium text-white">
                    Crypto buffers
                </h2>
            </div>

            <div className="flex items-start gap-2 text-xs text-muted-foreground md:w-[70%]">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                    The buffer is the safety margin taken off the market rate on
                    buy, sell and swap quotes. A swap uses the sell buffer of
                    the coin sold and the buy buffer of the coin bought. Coins
                    without a buffer cannot be quoted.
                </span>
            </div>

            {unconfigured.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive md:w-[70%]">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                        {unconfigured
                            .map(c => c.symbol.toUpperCase())
                            .join(', ')}{' '}
                        {unconfigured.length === 1 ? 'has' : 'have'} no buffer,
                        so buy, sell and swap quotes for{' '}
                        {unconfigured.length === 1 ? 'it' : 'them'} fail.
                    </span>
                </div>
            )}

            {/* Coin picker */}
            <div className="grid gap-3 sm:grid-cols-3 md:grid-cols-5">
                {sorted.map(crypto => {
                    const config = configs.byId[crypto.id]
                    const configured = isConfigured(config, crypto)
                    const active = crypto.id === selected?.id
                    return (
                        <button
                            key={crypto.id}
                            type="button"
                            onClick={() => setSelectedId(crypto.id)}
                            className={cn(
                                'flex items-center gap-3 rounded-[12px] border px-4 py-3 text-left transition-colors',
                                active
                                    ? 'border-[#17A2B8] bg-[#EDFBFD]'
                                    : 'border-border hover:bg-secondary'
                            )}
                        >
                            <CoinIcon crypto={crypto} />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium uppercase text-foreground">
                                    {crypto.symbol}
                                </p>
                                <p
                                    className={cn(
                                        'truncate text-xs',
                                        !config
                                            ? 'text-muted-foreground'
                                            : configured
                                              ? 'text-muted-foreground'
                                              : 'text-destructive'
                                    )}
                                >
                                    {!config
                                        ? '…'
                                        : !configured
                                          ? 'Not configured'
                                          : config.tiers.length > 0
                                            ? `${config.tiers.length} tier${config.tiers.length === 1 ? '' : 's'}${num(config.defaultBufferPercent) > 0 ? ` · ${num(config.defaultBufferPercent)}%` : ''}`
                                            : `Default ${num(config.defaultBufferPercent)}%`}
                                </p>
                            </div>
                        </button>
                    )
                })}
            </div>

            {selected &&
                (configs.byId[selected.id] ? (
                    // key resets the draft when switching coins
                    <BufferEditor
                        key={
                            selected.id +
                            JSON.stringify(configs.byId[selected.id])
                        }
                        config={configs.byId[selected.id]}
                    />
                ) : (
                    <Skeleton className="h-64 w-full md:w-[70%]" />
                ))}
        </div>
    )
}

// ---------------------------------------------------------------------------
// Editor for one coin
// ---------------------------------------------------------------------------

interface DraftTier {
    // undefined id = new (unsaved) tier
    id?: string
    orderType: BufferOrderType
    min: string
    max: string
    percent: string
}

const toDraft = (config: CryptoBufferConfig): DraftTier[] =>
    config.tiers.map(t => ({
        id: t.id,
        orderType: (t.orderType ?? 'BUY') as BufferOrderType,
        min: t.minAmount ?? '',
        max: t.maxAmount ?? '',
        percent: String(t.bufferPercent),
    }))

const validate = (
    defaultPct: string,
    maxPct: string,
    tiers: DraftTier[]
): { general: string[]; byTier: Record<number, string> } => {
    const general: string[] = []
    const byTier: Record<number, string> = {}
    const d = Number(defaultPct || 0)
    const m = Number(maxPct || 0)
    if (!(d >= 0 && d <= 50))
        general.push('Default buffer must be between 0 and 50%.')
    if (!(m >= 0 && m <= 100))
        general.push('Max buffer must be between 0 and 100%.')

    tiers.forEach((t, i) => {
        const min = Number(t.min)
        const max = Number(t.max)
        const pct = Number(t.percent)
        if (t.min === '' || t.max === '' || t.percent === '')
            byTier[i] = 'Fill in from, to and buffer.'
        else if (!(min >= 0) || !(max >= 0))
            byTier[i] = 'Amounts must be 0 or more.'
        else if (max < min)
            byTier[i] = '"To" must be greater than or equal to "From".'
        else if (!(pct >= 0 && pct <= 100))
            byTier[i] = 'Buffer must be between 0 and 100%.'
        else if (m > 0 && pct > m)
            byTier[i] = `Buffer is above the ${m}% max and will be capped.`
    })

    // Overlaps within the same side
    ;(['BUY', 'SELL'] as const).forEach(side => {
        const rows = tiers
            .map((t, i) => ({ i, min: Number(t.min), max: Number(t.max), t }))
            .filter(
                r => r.t.orderType === side && r.t.min !== '' && r.t.max !== ''
            )
            .sort((a, b) => a.min - b.min)
        for (let k = 1; k < rows.length; k++) {
            if (rows[k].min <= rows[k - 1].max && !byTier[rows[k].i]) {
                byTier[rows[k].i] = 'Overlaps another tier on this side.'
            }
        }
    })
    return { general, byTier }
}

const BufferEditor = ({ config }: { config: CryptoBufferConfig }) => {
    const symbol = config.symbol.toUpperCase()
    const patchMutation = usePatchCryptoBuffer()
    const createMutation = useCreateBufferTier()
    const deleteMutation = useDeleteBufferTier()

    const initialDefault = String(num(config.defaultBufferPercent))
    const initialMax = String(num(config.maxBufferPercent))
    const [defaultPct, setDefaultPct] = useState(initialDefault)
    const [maxPct, setMaxPct] = useState(initialMax)
    const [tiers, setTiers] = useState<DraftTier[]>(() => toDraft(config))
    const [deletedIds, setDeletedIds] = useState<string[]>([])
    const [saving, setSaving] = useState(false)

    const original = useMemo(() => toDraft(config), [config])
    const dirty =
        defaultPct !== initialDefault ||
        maxPct !== initialMax ||
        deletedIds.length > 0 ||
        JSON.stringify(tiers) !== JSON.stringify(original)

    const { general, byTier } = validate(defaultPct, maxPct, tiers)
    // "will be capped" is a warning, not a blocker
    const blocking =
        general.length > 0 ||
        Object.values(byTier).some(msg => !msg.includes('will be capped'))

    const update = (index: number, patch: Partial<DraftTier>) =>
        setTiers(prev =>
            prev.map((t, i) => (i === index ? { ...t, ...patch } : t))
        )

    const add = (orderType: BufferOrderType) =>
        setTiers(prev => [
            ...prev,
            { orderType, min: '', max: '', percent: '' },
        ])

    const remove = (index: number) => {
        const tier = tiers[index]
        if (tier.id) setDeletedIds(prev => [...prev, tier.id!])
        setTiers(prev => prev.filter((_, i) => i !== index))
    }

    const reset = () => {
        setDefaultPct(initialDefault)
        setMaxPct(initialMax)
        setTiers(toDraft(config))
        setDeletedIds([])
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            // Deletes first so edited/new tiers can't collide with removed ones.
            for (const tierId of deletedIds) {
                await deleteMutation.mutateAsync({
                    cryptoId: config.cryptoId,
                    tierId,
                })
            }

            const changedTiers = tiers
                .filter(t => t.id)
                .filter(t => {
                    const before = original.find(o => o.id === t.id)
                    return JSON.stringify(before) !== JSON.stringify(t)
                })
                .map(t => ({
                    id: t.id!,
                    minAmount: t.min,
                    maxAmount: t.max,
                    bufferPercent: Number(t.percent),
                }))

            const patch = {
                ...(defaultPct !== initialDefault && {
                    defaultBufferPercent: Number(defaultPct || 0),
                }),
                ...(maxPct !== initialMax && {
                    maxBufferPercent: Number(maxPct || 0),
                }),
                ...(changedTiers.length > 0 && { tiers: changedTiers }),
            }
            if (Object.keys(patch).length > 0) {
                await patchMutation.mutateAsync({
                    cryptoId: config.cryptoId,
                    data: patch,
                })
            }

            for (const t of tiers.filter(t => !t.id)) {
                await createMutation.mutateAsync({
                    cryptoId: config.cryptoId,
                    data: {
                        orderType: t.orderType,
                        minAmount: t.min,
                        maxAmount: t.max,
                        bufferPercent: Number(t.percent),
                    },
                })
            }

            toast.success(`${symbol} buffers saved`)
        } catch (err) {
            toast.error(`Failed to save ${symbol} buffers`, {
                description: getErrorMessage(err),
            })
        } finally {
            setSaving(false)
        }
    }

    const renderSide = (side: BufferOrderType, title: string, hint: string) => {
        const rows = tiers
            .map((tier, index) => ({ tier, index }))
            .filter(r => r.tier.orderType === side)
        return (
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#EDFBFD] px-6">
                    <h3 className="text-sm font-medium text-foreground">
                        {title}
                    </h3>
                </div>
                <div className="space-y-4 md:w-[70%]">
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{hint}</span>
                    </div>

                    {rows.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                            No tiers. The default buffer applies to every{' '}
                            {side === 'BUY' ? 'buy' : 'sell'} amount.
                        </p>
                    )}

                    {rows.map(({ tier, index }) => (
                        <div
                            key={tier.id ?? `new-${index}`}
                            className="space-y-2"
                        >
                            <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Amount range
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <RateInput
                                            placeholder="From"
                                            step="any"
                                            min={0}
                                            value={tier.min}
                                            onChange={e =>
                                                update(index, {
                                                    min: e.target.value,
                                                })
                                            }
                                        />
                                        <RateInput
                                            placeholder="To"
                                            step="any"
                                            min={0}
                                            value={tier.max}
                                            onChange={e =>
                                                update(index, {
                                                    max: e.target.value,
                                                })
                                            }
                                        />
                                        <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                                            <CoinIcon crypto={config} />
                                            <span className="text-sm font-medium text-foreground">
                                                {symbol}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Buffer
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <CurrencyInput
                                            placeholder="0"
                                            min={0}
                                            max={100}
                                            value={tier.percent}
                                            onChange={e =>
                                                update(index, {
                                                    percent: e.target.value,
                                                })
                                            }
                                            showPercentage
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => remove(index)}
                                            className="h-11 w-11 p-0 text-destructive hover:text-destructive"
                                            aria-label="Remove tier"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            {byTier[index] && (
                                <div
                                    className={cn(
                                        'flex items-start gap-2 rounded-lg border px-3 py-2 text-xs',
                                        byTier[index].includes('will be capped')
                                            ? 'border-amber-300 bg-amber-50 text-amber-700'
                                            : 'border-destructive/30 bg-destructive/5 text-destructive'
                                    )}
                                >
                                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <span>{byTier[index]}</span>
                                </div>
                            )}
                        </div>
                    ))}

                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => add(side)}
                        className="h-auto gap-2 p-0 text-sm text-[#5FA8B4] hover:bg-transparent hover:text-[#5FA8B4]/80"
                    >
                        <Plus className="h-4 w-4" />
                        Add {side === 'BUY' ? 'buy' : 'sell'} tier
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-8">
            {/* Defaults */}
            <div className="space-y-4">
                <div className="flex h-8 items-center gap-2 rounded-full bg-[#EDFBFD] px-6">
                    <CoinIcon crypto={config} />
                    <h3 className="text-sm font-medium text-foreground">
                        {config.name} ({symbol}) defaults
                    </h3>
                </div>

                <div className="grid gap-6 sm:grid-cols-2 md:w-[70%]">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Default buffer (%)
                        </label>
                        <CurrencyInput
                            placeholder="0"
                            min={0}
                            max={50}
                            value={defaultPct}
                            onChange={e => setDefaultPct(e.target.value)}
                            showPercentage
                        />
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                                Used for any amount no tier covers. Set this so
                                {` ${symbol} `}can always be quoted.
                            </span>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Max buffer (%)
                        </label>
                        <CurrencyInput
                            placeholder="0"
                            min={0}
                            max={100}
                            value={maxPct}
                            onChange={e => setMaxPct(e.target.value)}
                            showPercentage
                        />
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                                Caps any tier or default. 0 means no cap.
                            </span>
                        </div>
                    </div>
                </div>

                {general.map(msg => (
                    <div
                        key={msg}
                        className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive md:w-[70%]"
                    >
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{msg}</span>
                    </div>
                ))}
            </div>

            {renderSide(
                'BUY',
                'Buy tiers',
                `Applied when users buy ${symbol}, and to the coin received in a swap.`
            )}
            {renderSide(
                'SELL',
                'Sell tiers',
                `Applied when users sell ${symbol}, and to the coin sent in a swap.`
            )}

            {dirty && (
                <div className="flex items-center gap-3">
                    <Button
                        onClick={handleSave}
                        disabled={saving || blocking}
                        className="rounded-full bg-[#17A2B8] px-8 text-white hover:bg-[#1493a7]"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save changes'
                        )}
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={reset}
                        disabled={saving}
                        className="rounded-full px-6"
                    >
                        Discard
                    </Button>
                </div>
            )}
        </div>
    )
}
